package nl.volleyplanner.web.component

import kotlinx.datetime.format
import kotlinx.html.*
import kotlinx.html.stream.createHTML
import nl.volleyplanner.api.planner.ConstraintScoreImpact
import nl.volleyplanner.api.planner.PersonTaskStatistics
import nl.volleyplanner.api.planner.HardMediumSoftScore
import nl.volleyplanner.api.planner.TaskStatistics
import nl.volleyplanner.api.types.BigNumber
import nl.volleyplanner.web.DATE_TIME_FORMAT
import nl.volleyplanner.web.base.SimpleComponent
import nl.volleyplanner.web.base.icon
import nl.volleyplanner.web.textconverters.toUIString

fun FlowContent.scoreBadge(score: HardMediumSoftScore, block: (SPAN.() -> Unit)? = null) {
    span (classes = "vp-score-badges") {
        if (block != null) {
            block.invoke(this)
        }
        if (score.hardScore < BigNumber.ZERO) {
            span(classes = "ms-1 badge text-bg-danger") { +"${score.hardScore}" }
        }
        val badgeClass = when {
            score.softScore < BigNumber(-10, 3) -> "text-bg-warning"
            else -> "text-bg-secondary"
        }
        span(classes = "ms-1 badge $badgeClass") { +"${score.softScore}" }
    }
}

abstract class StatisticsScoreBadge() : SimpleComponent {
    fun FlowContent.taskAssignmentStatisticsTooltipPersonPart(personScoreImpacts: List<ConstraintScoreImpact>) {
        if (personScoreImpacts.isNotEmpty()) {
            div {
                p {
                    strong { +"Persoonlijke impact" }
                }
            }
            personScoreImpacts
                .sortedBy { it.impact }
                .forEach { impact ->
                    ul {
                        li { +impact.toUIString() }
                    }
                }
        }
    }

    fun FlowContent.taskAssignmentStatisticsTooltipTasksPart(assignedTasks: List<TaskStatistics>) {
        assignedTasks
            .sortedBy { it.task.startDateTime }
            .forEach { task ->
                div {
                    p {
                        strong {
                            +"${
                                task.task.startDateTime?.format(
                                    DATE_TIME_FORMAT
                                )
                            }: ${task.task.name}"
                        }
                    }
                    ul {
                        task.taskImpacts
                            .sortedBy { it.impact }
                            .forEach { scoreImpact ->
                                li { +scoreImpact.toUIString() }
                            }
                    }
                }
            }
    }
}
class PersonStatisticsScoreBadge(
    private val personStatistics: PersonTaskStatistics, private val taskStatisticsById: Map<String, TaskStatistics>
) : StatisticsScoreBadge() {
    private val personTaskStatistics = personStatistics.tasksById.mapNotNull { taskStatisticsById[it] }

    override fun render(flowContent: FlowContent) {
        val personTotalScore = personStatistics.totalScore
        val taskTotalScore = personTaskStatistics
            .map { it.totalScore }
            .reduceOrNull { acc, score -> acc + score } ?: HardMediumSoftScore.ZERO

        flowContent.div {
            icon("bi-person-fill")
            flowContent.scoreBadge(personTotalScore) {
                attributes["data-bs-toggle"] = "tooltip"
                attributes["data-bs-html"] = "true"
                attributes["data-bs-custom-class"] = "assignment-scores"
                attributes["data-bs-title"] = createHTML().div {
                    taskAssignmentStatisticsTooltipPersonPart(personStatistics.personImpacts)
                }
            }
        }
        flowContent.div {
            icon("bi-card-list")
            flowContent.scoreBadge(taskTotalScore) {
                attributes["data-bs-toggle"] = "tooltip"
                attributes["data-bs-html"] = "true"
                attributes["data-bs-custom-class"] = "assignment-scores"
                attributes["data-bs-title"] = createHTML().div {
                    taskAssignmentStatisticsTooltipTasksPart(personTaskStatistics)
                }
            }
        }
    }
}

class TaskOnlyStatisticsScoreBadge(private val taskStatistics: TaskStatistics) : StatisticsScoreBadge() {
    override fun render(flowContent: FlowContent) {
        flowContent.div {
            icon("bi-card-list")
            flowContent.scoreBadge(taskStatistics.totalScore) {
                attributes["data-bs-toggle"] = "tooltip"
                attributes["data-bs-html"] = "true"
                attributes["data-bs-custom-class"] = "assignment-scores"
                attributes["data-bs-title"] = createHTML().div {
                    taskAssignmentStatisticsTooltipTasksPart(listOf(taskStatistics))
                }
            }
        }
    }
}

class TaskAssignmentStatisticsScoreBadge(private val taskStatistics: TaskStatistics, private val personStatistics: PersonTaskStatistics?) : StatisticsScoreBadge() {
    override fun render(flowContent: FlowContent) {
        val personTotalScore = personStatistics?.totalScore

        flowContent.div(classes = "d-flex") {
            icon("bi-card-list")
            flowContent.scoreBadge(taskStatistics.totalScore) {
                attributes["data-bs-toggle"] = "tooltip"
                attributes["data-bs-html"] = "true"
                attributes["data-bs-custom-class"] = "assignment-scores"
                attributes["data-bs-title"] = createHTML().div {
                    taskAssignmentStatisticsTooltipTasksPart(listOf(taskStatistics))
                }
            }
        }
        if (personTotalScore != null) {
            flowContent.div(classes = "d-flex") {
                icon("bi-person-fill")
                flowContent.scoreBadge(personTotalScore) {
                    attributes["data-bs-toggle"] = "tooltip"
                    attributes["data-bs-html"] = "true"
                    attributes["data-bs-custom-class"] = "assignment-scores"
                    attributes["data-bs-title"] = createHTML().div {
                        taskAssignmentStatisticsTooltipPersonPart(personStatistics.personImpacts)
                    }
                }
            }
        }
    }
}